import { ShoppingCart, Menu, X, Download } from "lucide-react";
import logoElFaza from "@/assets/logo-elfaza-cropped.webp";
import { CartDrawer } from "@/components/CartDrawer";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/contexts/LanguageContext";
import { LangSwitcher } from "@/components/LangSwitcher";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const { lang, t } = useLang();

  const navLinks = lang === "en"
    ? [
        { label: "Home", path: "/en" },
        { label: "Our Story", path: "/en/our-story" },
        { label: "Solidarity", path: "/en#donations" },
        { label: "Contact", path: "/en/contact" },
      ]
    : [
        { label: "Accueil", path: "/" },
        { label: "Notre Histoire", path: "/notre-histoire" },
        { label: "Don Solidaire", path: "/#donations" },
        { label: "Contact", path: "/contact" },
      ];

  useEffect(() => {
    const checkAdmin = async (userId: string) => {
      const { data } = await supabase
        .from("user_roles").select("role")
        .eq("user_id", userId).eq("role", "admin").maybeSingle();
      setIsAdmin(!!data);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) checkAdmin(session.user.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        checkAdmin(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setInstallPrompt(null);
  };

  const installLabel = t({ fr: "Installer", en: "Install" });
  const adminLabel = t({ fr: "Admin", en: "Admin" });
  const menuOpenLabel = t({ fr: "Ouvrir le menu", en: "Open menu" });
  const menuCloseLabel = t({ fr: "Fermer le menu", en: "Close menu" });
  const installPath = lang === "en" ? "/en/install" : "/install";

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <Link to={lang === "en" ? "/en" : "/"} className="flex items-center gap-2">
          <img src={logoElFaza} alt="EL FAZA Canada" className="h-12 w-auto" width="161" height="48" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={(e) => {
                if (link.path.includes("#")) {
                  e.preventDefault();
                  const hash = link.path.split("#")[1];
                  const basePath = link.path.split("#")[0];
                  if (location.pathname !== basePath) {
                    window.location.href = link.path;
                  } else {
                    document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                  }
                }
              }}
              className={`font-body text-sm transition-colors hover:text-primary ${
                location.pathname === link.path.split("#")[0] && !link.path.includes("#")
                  ? "text-primary font-medium"
                  : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <Link
              to="/admin"
              className={`font-body text-sm transition-colors hover:text-primary ${
                location.pathname.startsWith("/admin") ? "text-primary font-medium" : "text-muted-foreground"
              }`}
            >
              {adminLabel}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <LangSwitcher variant="compact" className="sm:hidden" />
          <LangSwitcher variant="full" className="hidden sm:inline-flex" />
          {!isInstalled && (
            installPrompt ? (
              <Button
                size="sm"
                variant="outline"
                onClick={handleInstall}
                className="hidden sm:flex gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
              >
                <Download className="w-4 h-4" />
                {installLabel}
              </Button>
            ) : (
              <Link to={installPath} className="hidden sm:inline-flex">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
                >
                  <Download className="w-4 h-4" />
                  {installLabel}
                </Button>
              </Link>
            )
          )}
          <CartDrawer />
          <Button
            variant="ghost"
            size="icon"
            aria-label={mobileOpen ? menuCloseLabel : menuOpenLabel}
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={(e) => {
                  setMobileOpen(false);
                  if (link.path.includes("#")) {
                    e.preventDefault();
                    const hash = link.path.split("#")[1];
                    const basePath = link.path.split("#")[0];
                    if (location.pathname !== basePath) {
                      window.location.href = link.path;
                    } else {
                      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
                    }
                  }
                }}
                className={`font-body text-sm py-2 transition-colors ${
                  location.pathname === link.path.split("#")[0] && !link.path.includes("#")
                    ? "text-primary font-medium"
                    : "text-muted-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className={`font-body text-sm py-2 transition-colors ${
                  location.pathname.startsWith("/admin") ? "text-primary font-medium" : "text-muted-foreground"
                }`}
              >
                {adminLabel}
              </Link>
            )}
            <div className="pt-2">
              <LangSwitcher variant="full" />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
